import { useSyncExternalStore } from "react";

// The globally selected date range (lookback window). Like the perspective
// lens, it lives in localStorage so route loaders can read it when building
// query variables; the header switcher writes it and invalidates the router.
// Anchored to today: "Last 1y" = the 12 months ending now.
export type DateRange = "1Y" | "2Y" | "5Y" | "ALL";

const KEY = "cxcash.dateRange";
const DEFAULT: DateRange = "ALL";
const VALUES: DateRange[] = ["1Y", "2Y", "5Y", "ALL"];

export const DATE_RANGE_LABELS: Record<DateRange, string> = {
  "1Y": "Last 1y",
  "2Y": "Last 2y",
  "5Y": "Last 5y",
  ALL: "All time",
};

const listeners = new Set<() => void>();

export function getDateRange(): DateRange {
  const stored = localStorage.getItem(KEY);
  return VALUES.includes(stored as DateRange) ? (stored as DateRange) : DEFAULT;
}

export function setDateRange(value: DateRange) {
  localStorage.setItem(KEY, value);
  for (const listener of listeners) listener();
}

export function useDateRange(): DateRange {
  return useSyncExternalStore(
    (callback) => {
      listeners.add(callback);
      return () => listeners.delete(callback);
    },
    () => getDateRange()
  );
}

// The inclusive lower bound (YYYY-MM-DD) for a range, or null for "all time".
export function dateRangeFromISO(value: DateRange): string | null {
  if (value === "ALL") return null;
  const years = value === "1Y" ? 1 : value === "2Y" ? 2 : 5;
  const date = new Date();
  date.setFullYear(date.getFullYear() - years);
  return date.toISOString().slice(0, 10);
}

// The same window expressed as a month count for series resolvers that take
// `months` rather than a date. "All time" maps to the app's 10-year default.
export function dateRangeMonths(value: DateRange): number {
  switch (value) {
    case "1Y":
      return 12;
    case "2Y":
      return 24;
    case "5Y":
      return 60;
    default:
      return 120;
  }
}
